import React from 'react'

import Button from 'presentation/shared/components/Button'
import TextArea from 'presentation/shared/components/TextArea'
import TextField from 'presentation/shared/components/TextField'
import SelectPendencyStatus from '../SelectPendencyStatus'
import { DoctorCouncilRegister } from 'domain/entities/council-register-model'
import { DoctorSpecialty } from 'domain/entities/doctor-specialty-model'

import * as S from './styles'
import { useFormik } from 'formik'
import { DoctorRegistrationSteps } from 'common/enum/doctor-overall-registration-steps'
import { toast } from 'react-toastify'
import { translateAcademicFormation } from 'presentation/utils/translators/academic-formation'
import moment from 'moment-timezone'
import { DoctorRegisterStepStatus } from 'domain/entities/doctor-register'
import {
  disableObservation,
  generateNewRegistrationDetailsValidationSchema
} from 'presentation/hospital/layouts/DoctorRegister/NewRegistrationsDetails'
import { newRegistrationDetailsContext } from 'presentation/hospital/layouts/DoctorRegister/NewRegistrationsDetails/context'
import SupportText from 'presentation/shared/components/SupportText'
import { MedicalTeam } from 'domain/entities/MedicalTeam'
import { StudyModality } from 'common/enum/study-modality'

type ProfessionalDataProps = {
  professionalData?: {
    isStudentDoctor?: boolean
    modality?: string
    startCollegeDate?: string
    endCollegePrediction?: string
    academicFormation?: string
    educationLevel?: string
    academicInstitution?: string
    specialty?: string
    secondarySpecialty?: DoctorSpecialty[]
    lattes?: string
    linkedin?: string
    councilRegister?: DoctorCouncilRegister
    additionalCouncilRegisters?: [
      {
        crm?: string
        crmUF?: string
        crmIssueDate?: string
      }
    ]
    professionalObservation?: string
    academicObservation?: string
    specialtiesObservation?: string
  }
  initialValues: registerPendencyValues
  updateStepStatus?: (step: any, initialValues: any) => Promise<void>
  isDirector: boolean
  teams?: MedicalTeam[]
}

const ProfessionalData = ({
  professionalData,
  initialValues,
  updateStepStatus,
  isDirector,
  teams
}: ProfessionalDataProps) => {
  const { isStepsUpdateBlocked } = newRegistrationDetailsContext

  const setPendencyStatus = (field: string, event: any) => {
    if (isDirector) {
      setFieldValue(
        `${field}.status`,
        event.target.value === 'Revisão'
          ? DoctorRegisterStepStatus.REVIEW
          : DoctorRegisterStepStatus.APPROVED_DIRECTOR
      )
    } else {
      setFieldValue(
        `${field}.status`,
        event.target.value === 'Pendente'
          ? DoctorRegisterStepStatus.PENDENT
          : DoctorRegisterStepStatus.APPROVED_DOCTOR_REGISTER
      )
    }
  }

  const { handleChange, values, setFieldValue, handleSubmit, isValid } =
    useFormik({
      initialValues: initialValues,
      validateOnMount: true,
      validationSchema: !isDirector
        ? generateNewRegistrationDetailsValidationSchema(
            DoctorRegistrationSteps.ACADEMIC_EDUCATION,
            DoctorRegistrationSteps.PROFESSIONAL_DATA,
            DoctorRegistrationSteps.SPECIALTIES
          )
        : generateNewRegistrationDetailsValidationSchema(
            DoctorRegistrationSteps.ACADEMIC_EDUCATION,
            DoctorRegistrationSteps.PROFESSIONAL_DATA,
            DoctorRegistrationSteps.SPECIALTIES,
            DoctorRegistrationSteps.TEAMS
          ),
      onSubmit: async (values) => {
        try {
          if (!isDirector) {
            delete values[DoctorRegistrationSteps.TEAMS]
          }
          await updateStepStatus?.(values, initialValues)
          toast.success('Dados enviados com sucesso.')
          window.scrollTo(0, 0)
        } catch (error) {
          toast.error('Erro ao salvar dados.')
        }
      }
    })

  const formatCouncilRegisterIssueDate = (issueDate?: string | Date) => {
    if (!issueDate) return ''

    return moment(issueDate).utcOffset(3).format('DD/MM/YYYY')
  }

  return (
    <S.Wrapper data-testid="card-professional-data">
      <S.ProfessionalDataContainer>
        <S.PendencySelection>
          <SelectPendencyStatus
            handleChange={(e) =>
              setPendencyStatus(DoctorRegistrationSteps.PROFESSIONAL_DATA, e)
            }
            initialValue={
              values[DoctorRegistrationSteps.PROFESSIONAL_DATA].status
            }
            data-testid="select-pendency-status-professional-data"
            disabled={isStepsUpdateBlocked}
            isDirector={isDirector}
          />
        </S.PendencySelection>
        <S.Title>Dados profissionais</S.Title>
        <S.InputsContainer>
          <TextField
            label="Médico estudante da rede Matei Dei?"
            value={professionalData?.isStudentDoctor ? 'Sim' : 'Não'}
            required
            disabled
            data-testid="input-is-student-doctor"
          />

          <TextField
            label="Modalidade de ensino"
            value={
              professionalData?.modality &&
              (professionalData?.modality === StudyModality.RESIDENT
                ? 'Residente | '
                : 'Especializando | ')
            }
            required
            disabled
            data-testid="input-modality"
          />
          <TextField
            label="Data de início"
            value={professionalData?.startCollegeDate}
            required
            disabled
            data-testid="input-start-collegege-date"
          />
          <TextField
            label="Previsão de término"
            value={professionalData?.endCollegePrediction}
            required
            disabled
            data-testid="input-end-college-prediction"
          />
        </S.InputsContainer>
        <S.DirectorObservations>
          <TextArea
            label="Observações do diretor"
            name={`${DoctorRegistrationSteps.PROFESSIONAL_DATA}.director_review`}
            value={
              values[DoctorRegistrationSteps.PROFESSIONAL_DATA].director_review
            }
            onChange={handleChange}
            required
            disabled={
              !isDirector ||
              values[DoctorRegistrationSteps.PROFESSIONAL_DATA].status !==
                DoctorRegisterStepStatus.REVIEW
            }
            data-testid="input-director-observation-professional-data"
          />
        </S.DirectorObservations>
        {!isDirector && (
          <S.PendencyJustification>
            <TextArea
              name={`${DoctorRegistrationSteps.PROFESSIONAL_DATA}.description`}
              label="No caso de pendência, favor descrever"
              required
              value={
                values[DoctorRegistrationSteps.PROFESSIONAL_DATA].description
              }
              onChange={handleChange}
              data-testid="input-pendency-professional-data"
              disabled={
                disableObservation(DoctorRegistrationSteps.PROFESSIONAL_DATA) ||
                values[DoctorRegistrationSteps.PROFESSIONAL_DATA].status !==
                  DoctorRegisterStepStatus.PENDENT ||
                isStepsUpdateBlocked
              }
            />
          </S.PendencyJustification>
        )}
      </S.ProfessionalDataContainer>
      <S.AcademicFormationContainer>
        <S.PendencySelection>
          <SelectPendencyStatus
            handleChange={(e) =>
              setPendencyStatus(DoctorRegistrationSteps.ACADEMIC_EDUCATION, e)
            }
            initialValue={
              values[DoctorRegistrationSteps.ACADEMIC_EDUCATION].status
            }
            data-testid="select-pendency-status-academic-formation"
            disabled={isStepsUpdateBlocked}
            isDirector={isDirector}
          />
        </S.PendencySelection>
        <S.Title>Formação acadêmica</S.Title>
        <S.InputsContainer>
          <TextField
            label="Formação profissional"
            value={translateAcademicFormation(
              professionalData?.academicFormation ?? ''
            )}
            required
            disabled
            data-testid="input-academic-formation"
          />
          <TextField
            label="Instituição acadêmica (graduação)"
            value={professionalData?.academicInstitution ?? ''}
            required
            disabled
            data-testid="input-academic-institution"
          />
          <TextField
            label="Número de registro no conselho"
            value={professionalData?.councilRegister?.crm ?? ''}
            required
            disabled
            data-testid="input-council-register-number"
          />
          <section>
            <TextField
              label="UF"
              value={professionalData?.councilRegister?.crmUf ?? ''}
              required
              disabled
              data-testid="input-council-register-district"
            />
            <TextField
              label="Data de emissão"
              value={formatCouncilRegisterIssueDate(
                professionalData?.councilRegister?.issueDate
              )}
              required
              disabled
              data-testid="input-council-register-issue-date"
            />
          </section>
        </S.InputsContainer>
        <S.InputsContainer>
          {professionalData?.additionalCouncilRegisters &&
            professionalData.additionalCouncilRegisters.map(
              (councilRegister, idx) => (
                <div key={councilRegister.crm}>
                  <TextField
                    label={`Nº de registro do conselho secundário ${idx + 1}`}
                    value={councilRegister.crm ?? ''}
                    required
                    disabled
                    data-testid={`input-additional-council-register-number-${idx}`}
                  />
                  <section>
                    <TextField
                      label="UF"
                      value={councilRegister.crmUF ?? ''}
                      required
                      disabled
                      data-testid={`input-additional-council-register-district-${idx}`}
                    />
                    <TextField
                      label="Data de emissão"
                      value={councilRegister.crmIssueDate ?? ''}
                      required
                      disabled
                      data-testid={`input-additional-council-register-issue-date-${idx}`}
                    />
                  </section>
                </div>
              )
            )}
        </S.InputsContainer>
        <S.InputsContainer>
          <TextField
            label="Currículo lattes"
            value={professionalData?.lattes ?? ''}
            required
            disabled
            data-testid="input-lattes"
          />
          <TextField
            label="Perfil do linkedin"
            value={professionalData?.linkedin}
            required
            disabled
            data-testid="input-linkedin"
          />
        </S.InputsContainer>
        <S.DirectorObservations>
          <TextArea
            label="Observações do diretor"
            name={`${DoctorRegistrationSteps.ACADEMIC_EDUCATION}.director_review`}
            value={
              values[DoctorRegistrationSteps.ACADEMIC_EDUCATION].director_review
            }
            onChange={handleChange}
            required
            disabled={
              !isDirector ||
              values[DoctorRegistrationSteps.ACADEMIC_EDUCATION].status !==
                DoctorRegisterStepStatus.REVIEW
            }
            data-testid="input-director-observation-academic-formation"
          />
        </S.DirectorObservations>
        {!isDirector && (
          <S.PendencyJustification>
            <TextArea
              label="No caso de pendência, favor descrever"
              name={`${DoctorRegistrationSteps.ACADEMIC_EDUCATION}.description`}
              required
              value={
                values[DoctorRegistrationSteps.ACADEMIC_EDUCATION].description
              }
              onChange={handleChange}
              data-testid="input-pendency-academic-formation"
              disabled={
                disableObservation(
                  DoctorRegistrationSteps.ACADEMIC_EDUCATION
                ) ||
                values[DoctorRegistrationSteps.ACADEMIC_EDUCATION].status !==
                  DoctorRegisterStepStatus.PENDENT ||
                isStepsUpdateBlocked
              }
            />
          </S.PendencyJustification>
        )}
      </S.AcademicFormationContainer>
      <S.SpecialtiesContainer>
        <S.PendencySelection>
          <SelectPendencyStatus
            handleChange={(e) =>
              setPendencyStatus(DoctorRegistrationSteps.SPECIALTIES, e)
            }
            initialValue={values[DoctorRegistrationSteps.SPECIALTIES].status}
            data-testid="select-pendency-status-specialties"
            disabled={isStepsUpdateBlocked}
            isDirector={isDirector}
          />
        </S.PendencySelection>
        <S.Title>Especialidades</S.Title>
        <S.InputsContainer>
          <TextField
            label="Especialidade principal"
            value={professionalData?.specialty ?? ''}
            required
            disabled
            data-testid="input-specialty"
          />
          <TextField
            label="Especialidade secundárias"
            value={
              professionalData?.secondarySpecialty
                ? professionalData.secondarySpecialty
                    .map((specialty) => specialty.name)
                    .join()
                : ''
            }
            required
            disabled
            data-testid="input-secondary-specialties"
          />
        </S.InputsContainer>
        <S.DirectorObservations>
          <TextArea
            label="Observações do diretor"
            name={`${DoctorRegistrationSteps.SPECIALTIES}.director_review`}
            value={values[DoctorRegistrationSteps.SPECIALTIES].director_review}
            onChange={handleChange}
            required
            disabled={
              !isDirector ||
              values[DoctorRegistrationSteps.SPECIALTIES].status !==
                DoctorRegisterStepStatus.REVIEW
            }
            data-testid="input-director-observation-specialties"
          />
        </S.DirectorObservations>
        {!isDirector && (
          <S.PendencyJustification>
            <TextArea
              name={`${DoctorRegistrationSteps.SPECIALTIES}.description`}
              label="No caso de pendência, favor descrever"
              required
              value={values[DoctorRegistrationSteps.SPECIALTIES].description}
              onChange={handleChange}
              data-testid="input-pendency-specialties"
              disabled={
                disableObservation(DoctorRegistrationSteps.SPECIALTIES) ||
                values[DoctorRegistrationSteps.SPECIALTIES].status !==
                  DoctorRegisterStepStatus.PENDENT ||
                isStepsUpdateBlocked
              }
            />
          </S.PendencyJustification>
        )}
      </S.SpecialtiesContainer>
      {isDirector && (
        <S.SpecialtiesContainer>
          <S.PendencySelection style={{ marginTop: '24px' }}>
            <SelectPendencyStatus
              handleChange={(e) =>
                setPendencyStatus(DoctorRegistrationSteps.TEAMS, e)
              }
              initialValue={
                values[DoctorRegistrationSteps.TEAMS]?.status ??
                DoctorRegisterStepStatus.PENDENT
              }
              data-testid="select-pendency-status-acting-sectors"
              disabled={isStepsUpdateBlocked}
              isDirector={isDirector}
            />
          </S.PendencySelection>
          <S.Title>Equipes</S.Title>
          <S.TeamsContainer>
            <SupportText color="primary">
              Equipes selecionadas pelo profissional
            </SupportText>
            <ul>
              {teams &&
                teams.map((team, key) => <li key={key}>{team.description}</li>)}
            </ul>
          </S.TeamsContainer>
          <S.DirectorObservations>
            <TextArea
              label="Observações do diretor"
              name={`${DoctorRegistrationSteps.TEAMS}.director_review`}
              value={values[DoctorRegistrationSteps.TEAMS]?.director_review}
              onChange={handleChange}
              required
              disabled={
                !isDirector ||
                values[DoctorRegistrationSteps.TEAMS]?.status !==
                  DoctorRegisterStepStatus.REVIEW
              }
              data-testid="input-director-observation-specialties"
            />
          </S.DirectorObservations>
        </S.SpecialtiesContainer>
      )}
      <S.ActionsButtons>
        <Button
          data-testid="btn-save-professional-data"
          onClick={() => handleSubmit()}
          variant={isValid && !isStepsUpdateBlocked ? 'outlined' : undefined}
          disabled={!isValid || isStepsUpdateBlocked}
        >
          Salvar
        </Button>
      </S.ActionsButtons>
    </S.Wrapper>
  )
}

type registerPendencyValues = {
  [DoctorRegistrationSteps.PROFESSIONAL_DATA]: {
    description?: string
    director_review?: string
    status: DoctorRegisterStepStatus
  }
  [DoctorRegistrationSteps.ACADEMIC_EDUCATION]: {
    description?: string
    director_review?: string
    status: DoctorRegisterStepStatus
  }
  [DoctorRegistrationSteps.SPECIALTIES]: {
    description?: string
    director_review?: string
    status: DoctorRegisterStepStatus
  }
  [DoctorRegistrationSteps.TEAMS]?: {
    director_review?: string
    status: DoctorRegisterStepStatus
  }
}

export default ProfessionalData
